import 'angular-fallback-image';
import 'angular-scroll';
import 'babel-polyfill';
import envConfig from 'config.env';
import angular from 'angular';
import angularSanitize from 'angular-sanitize';
import angularTouch from 'angular-touch';
import angularLocalStorage from 'angular-local-storage';
import angularRecaptcha from 'angular-recaptcha';
import angularMoment from 'angular-moment';
import ngAnimate from 'angular-animate';
import uiRouter from '@uirouter/angularjs';
import restangular from 'restangular';
import modal from 'angular-ui-bootstrap/src/modal/index-nocss.js';
import ComponentModule from './components';
import ContainerModule from './containers';
import ServicesModule from './services';
import ConstantsModule from './constants';
import FiltersModules from './filters';
import {
  buildQueryParamUrl,
  getTcomConfigParam
} from './utils';

import './root.less';

const RootModule = angular
  .module('ExpertFinder', [
    uiRouter,
    angularSanitize,
    angularTouch,
    angularLocalStorage,
    angularRecaptcha,
    restangular,
    angularMoment,
    modal,
    ngAnimate,
    'angular-fallback-image',
    'duScroll',
    ConstantsModule.name,
    ComponentModule.name,
    ContainerModule.name,
    ServicesModule.name,
    FiltersModules.name
  ])
  .config(
    /* @ngInject */
    ($windowProvider,
      $stateProvider,
      localStorageServiceProvider,
      $urlRouterProvider,
      $locationProvider,
      RestangularProvider,
      SEARCH_PARAMS) => {
      // This initial abstract route is used to fetch
      // the configuration async before loading

      // Having root.xyz means the abstract root state
      // will get executed as a parent
      $stateProvider
        .state('root', {
          abstract: true,
          template: '<ui-view ng-cloak class="ng-cloak"/>',
          resolve: {
            config: ConfigurationService => (
              ConfigurationService.bootstrap
            )
          }
        })
        .state('root.search', {
          url: '/',
          component: 'searchContainer',
          resolve: {
            config: ProductLinkService => (
              ProductLinkService.setProductLinkVerification(false)
            )
          }
        })
        .state('root.results', {
          url: buildQueryParamUrl('/results', SEARCH_PARAMS),
          component: 'resultsContainer',
          data: {
            localStorageRequired: true
          },
          params: {
            isProductLink: null
          }
        })
        .state('root.expert', {
          url: '/expert/:expertID',
          component: 'expertContainer',
          data: {
            localStorageRequired: true
          },
          params: {
            isProductLink: null
          }
        })
        .state('root.contact-expert', {
          url: '/expert/:expertID/contact',
          component: 'expertContactContainer',
          data: {
            localStorageRequired: true,
          },
          params: {
            isProductLink: null
          }
        })
        .state('root.contact-success', {
          url: '/contact-expert/success',
          component: 'contactExpertSuccess',
          params: {
            id: null
          }
        })
        .state('root.contact-error', {
          url: '/contact-expert/error',
          component: 'errorContainer'
        })
        .state('error', {
          url: '/error',
          component: 'errorContainer'
        });

      $urlRouterProvider.otherwise('/');

      $locationProvider.hashPrefix('');

      localStorageServiceProvider
        .setPrefix('expertfinder');

      // TODO - Abstract into utility helper to fetch on window
      // Fetch recaptcha site key in similar way
      const $window = $windowProvider.$get();
      const baseUrl = getTcomConfigParam($window, 'microServiceUrl') || envConfig.baseUrl;

      RestangularProvider
        .setBaseUrl(baseUrl);
    }
  )
  .run(/* @ngInject */(
      $transitions,
      DataService,
      MANDATORY_SEARCH_PARAMS,
      DIRECT_LINK_SEARCH_PARAMS,
      ConfigurationService,
      ProductLinkService
    ) => {
    // After loading config, validate what is held in localStorage
    // to ensure consistency with AEM params
    ConfigurationService
      .bootstrap
      .then((config) => {
        if (!DataService.validate(config)) {
          DataService.clearData();
        }
      });

    $transitions.onStart({
      // localStorageRequired is set for all states that retrieve information from the microservice
      // such as results, expert profile, expert contact
      to: state => state.data && state.data.localStorageRequired
    }, (trans) => {
      const transitionParams = trans.params();
      // When specifically going to an experts details
      const hasExpertId = !!transitionParams.expertID;

      // For when the url is a product link coming from the AEM product pages
      // In this case we want to bypass the requirement for localStorage
      const directLinkQueryParamsExist = DIRECT_LINK_SEARCH_PARAMS
        .every(paramName => transitionParams[paramName]);

      // For all other types of standard transition behaviours
      // that require checking of mandatory params
      const queryParamsExist = MANDATORY_SEARCH_PARAMS
        .every(paramName => transitionParams[paramName]);

      // this handles hitting the back button in the browser when the user
      // has come from a product link and is on the contact expert page
      // This should only occur when the user has come from a product page elsewhere on t.com
      if (ProductLinkService.getProductLinkVerification()) {
        return true;
      }

      // this handles transitioning to an expert when the user has come from a product link
      // the 'isProductLink' flag is inserted from the result.controller
      if (transitionParams.expertID && transitionParams.isProductLink) {
        return true;
      }

      // this handles navigating to search results from a product link in AEM
      if (transitionParams.isProductLink) {
        ProductLinkService.setProductLinkVerification(true);
        ProductLinkService.setSearchParams(transitionParams);
        return directLinkQueryParamsExist;
      }
      // For all other link types where we want to make sure that there is saved local data
      ProductLinkService.setProductLinkVerification(false);
      const data = DataService.getData();
      const hasData = Object.keys(data).length > 0;

      const isTransitioningToSearchPageWithAllQueryParams = queryParamsExist && hasData;

      // Retrieve an experts data
      const isTransitioningToExpertPage = hasExpertId && hasData;

      // All paths that require Mandatory Search Params
      // If the search params do not exist then
      // the application will always default to the search page
      return isTransitioningToSearchPageWithAllQueryParams || isTransitioningToExpertPage || trans.router.stateService.target('root.search');
    });
  });

export default RootModule;



// WEBPACK FOOTER //
// ./src/root.module.js